/**
 * "By Unit" income grid — rows are units grouped by property, columns are the
 * months in the selected range. Each cell shows the rent collected for that
 * unit in that month. Per-unit totals + monthly averages live in trailing
 * columns; a footer row surfaces the month totals + grand total + grand avg.
 *
 * Data source: reuses `/api/income?year=YYYY` for each year that overlaps the
 * selected range. `/api/income` already returns per-month → per-property →
 * per-unit breakdowns, so no new backend is required.
 */
import { useEffect, useMemo, useState } from 'react';
import { getIncome } from '@/lib/api';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Download, TrendingUp } from 'lucide-react';

const MONTHS = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
const fmt = (n) => (n || 0).toLocaleString(undefined, { maximumFractionDigits: 0 });

function monthLabel(year, month) {
  return `${MONTHS[month - 1]} '${String(year).slice(-2)}`;
}

function enumerateMonths(startY, startM, endY, endM) {
  const out = [];
  let y = startY, m = startM;
  while (y < endY || (y === endY && m <= endM)) {
    out.push({ year: y, month: m });
    m += 1;
    if (m > 12) { m = 1; y += 1; }
    if (out.length > 240) break; // 20-year safety
  }
  return out;
}

export default function IncomeByUnitView() {
  const now = new Date();
  const curY = now.getFullYear();
  const curM = now.getMonth() + 1;
  const [startYear, setStartYear] = useState(curY);
  const [startMonth, setStartMonth] = useState(1);
  const [endYear, setEndYear] = useState(curY);
  const [endMonth, setEndMonth] = useState(curM);
  const [rawByYear, setRawByYear] = useState({}); // { year: payload }
  const [loading, setLoading] = useState(false);

  // Years we need to fetch
  const yearsInRange = useMemo(() => {
    const out = [];
    for (let y = startYear; y <= endYear; y++) out.push(y);
    return out;
  }, [startYear, endYear]);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      if (startYear > endYear || (startYear === endYear && startMonth > endMonth)) return;
      setLoading(true);
      try {
        const missing = yearsInRange.filter((y) => !rawByYear[y]);
        if (missing.length > 0) {
          const fetched = await Promise.all(missing.map((y) => getIncome(y).then((d) => [y, d])));
          if (!cancelled) {
            setRawByYear((prev) => {
              const next = { ...prev };
              for (const [y, d] of fetched) next[y] = d;
              return next;
            });
          }
        }
      } catch { toast.error('Failed to load income data'); }
      finally { if (!cancelled) setLoading(false); }
    })();
    return () => { cancelled = true; };
  }, [yearsInRange, startYear, startMonth, endYear, endMonth, rawByYear]);

  // Shape: rows = units grouped by property, cells keyed by "yyyy-mm"
  const { cols, propertyRows, colTotals, grandTotal } = useMemo(() => {
    const cols = enumerateMonths(startYear, startMonth, endYear, endMonth);
    const propMap = new Map(); // property_id → { name, units: Map<unit_id, {unit_number, cells}> }
    for (const col of cols) {
      const payload = rawByYear[col.year];
      if (!payload) continue;
      const m = payload.months.find((x) => x.month === col.month);
      if (!m) continue;
      for (const p of m.properties) {
        if (!propMap.has(p.property_id)) {
          propMap.set(p.property_id, { property_id: p.property_id, property_name: p.property_name, units: new Map() });
        }
        const prec = propMap.get(p.property_id);
        for (const u of p.units) {
          if (!prec.units.has(u.unit_id)) {
            prec.units.set(u.unit_id, { unit_id: u.unit_id, unit_number: u.unit_number, cells: {} });
          }
          const ukey = `${col.year}-${col.month}`;
          prec.units.get(u.unit_id).cells[ukey] = (prec.units.get(u.unit_id).cells[ukey] || 0) + (u.total || 0);
        }
      }
    }
    const propertyRows = [...propMap.values()]
      .map((p) => ({
        ...p,
        units: [...p.units.values()].sort((a, b) => {
          const an = parseInt(a.unit_number, 10); const bn = parseInt(b.unit_number, 10);
          if (!isNaN(an) && !isNaN(bn)) return an - bn;
          return (a.unit_number || '').localeCompare(b.unit_number || '');
        }),
      }))
      .sort((a, b) => (a.property_name || '').localeCompare(b.property_name || ''));

    const colTotals = cols.map((col) => {
      const key = `${col.year}-${col.month}`;
      let sum = 0;
      for (const p of propertyRows) for (const u of p.units) sum += (u.cells[key] || 0);
      return sum;
    });
    const grandTotal = colTotals.reduce((a, b) => a + b, 0);
    return { cols, propertyRows, colTotals, grandTotal };
  }, [rawByYear, startYear, startMonth, endYear, endMonth]);

  const monthCount = cols.length || 1;
  const grandAvg = grandTotal / monthCount;

  const downloadCSV = () => {
    const rows = [];
    rows.push(['Property', 'Unit', ...cols.map((c) => monthLabel(c.year, c.month)), 'Total', 'Avg / mo']);
    for (const p of propertyRows) {
      for (const u of p.units) {
        const cells = cols.map((c) => u.cells[`${c.year}-${c.month}`] || 0);
        const total = cells.reduce((a, b) => a + b, 0);
        rows.push([p.property_name, `Unit ${u.unit_number}`, ...cells.map((x) => Math.round(x).toString()), Math.round(total).toString(), Math.round(total / monthCount).toString()]);
      }
    }
    rows.push(['TOTAL', '', ...colTotals.map((x) => x.toFixed(2)), grandTotal.toFixed(2), grandAvg.toFixed(2)]);
    const csv = rows.map((r) => r.map((c) => `"${String(c).replace(/"/g, '""')}"`).join(',')).join('\n');
    const blob = new Blob([csv], { type: 'text/csv' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url; a.download = `income-by-unit_${startYear}-${String(startMonth).padStart(2,'0')}_to_${endYear}-${String(endMonth).padStart(2,'0')}.csv`;
    a.click();
    URL.revokeObjectURL(url);
  };

  const setQuickRange = (label) => {
    if (label === 'ytd') { setStartYear(curY); setStartMonth(1); setEndYear(curY); setEndMonth(curM); }
    else if (label === 'thisYear') { setStartYear(curY); setStartMonth(1); setEndYear(curY); setEndMonth(12); }
    else if (label === 'lastYear') { setStartYear(curY - 1); setStartMonth(1); setEndYear(curY - 1); setEndMonth(12); }
    else if (label === 'last12') {
      let y = curY, m = curM - 11;
      while (m <= 0) { m += 12; y -= 1; }
      setStartYear(y); setStartMonth(m); setEndYear(curY); setEndMonth(curM);
    }
    else if (label === 'last3') {
      let y = curY, m = curM - 2;
      while (m <= 0) { m += 12; y -= 1; }
      setStartYear(y); setStartMonth(m); setEndYear(curY); setEndMonth(curM);
    }
  };

  const yearOptions = useMemo(() => {
    const out = [];
    for (let y = curY - 6; y <= curY + 2; y++) out.push(y);
    return out;
  }, [curY]);

  return (
    <div className="space-y-6" data-testid="income-by-unit-view">
      {/* Filters bar */}
      <div className="rounded-md border border-slate-200 bg-white shadow-sm">
        <div className="px-4 py-3 border-b border-slate-200 flex flex-wrap items-end gap-4">
          <div className="grid grid-cols-2 gap-2">
            <div>
              <Label className="text-[10px] uppercase tracking-wider text-slate-500 mb-1">From</Label>
              <div className="flex gap-1">
                <Select value={String(startMonth)} onValueChange={(v) => setStartMonth(Number(v))}>
                  <SelectTrigger className="w-[90px] h-9 text-sm" data-testid="by-unit-start-month"><SelectValue /></SelectTrigger>
                  <SelectContent>{MONTHS.map((m, i) => <SelectItem key={i} value={String(i + 1)}>{m}</SelectItem>)}</SelectContent>
                </Select>
                <Select value={String(startYear)} onValueChange={(v) => setStartYear(Number(v))}>
                  <SelectTrigger className="w-[90px] h-9 text-sm" data-testid="by-unit-start-year"><SelectValue /></SelectTrigger>
                  <SelectContent>{yearOptions.map((y) => <SelectItem key={y} value={String(y)}>{y}</SelectItem>)}</SelectContent>
                </Select>
              </div>
            </div>
            <div>
              <Label className="text-[10px] uppercase tracking-wider text-slate-500 mb-1">To</Label>
              <div className="flex gap-1">
                <Select value={String(endMonth)} onValueChange={(v) => setEndMonth(Number(v))}>
                  <SelectTrigger className="w-[90px] h-9 text-sm" data-testid="by-unit-end-month"><SelectValue /></SelectTrigger>
                  <SelectContent>{MONTHS.map((m, i) => <SelectItem key={i} value={String(i + 1)}>{m}</SelectItem>)}</SelectContent>
                </Select>
                <Select value={String(endYear)} onValueChange={(v) => setEndYear(Number(v))}>
                  <SelectTrigger className="w-[90px] h-9 text-sm" data-testid="by-unit-end-year"><SelectValue /></SelectTrigger>
                  <SelectContent>{yearOptions.map((y) => <SelectItem key={y} value={String(y)}>{y}</SelectItem>)}</SelectContent>
                </Select>
              </div>
            </div>
          </div>
          <div className="flex items-end gap-1 flex-wrap">
            {[
              { id: 'ytd', label: 'YTD' },
              { id: 'last3', label: 'Last 3 mo' },
              { id: 'last12', label: 'Last 12 mo' },
              { id: 'thisYear', label: 'This year' },
              { id: 'lastYear', label: 'Last year' },
            ].map((q) => (
              <Button
                key={q.id}
                variant="outline"
                size="sm"
                className="h-9 text-xs border-slate-200 hover:bg-slate-50"
                onClick={() => setQuickRange(q.id)}
                data-testid={`by-unit-quick-${q.id}`}
              >
                {q.label}
              </Button>
            ))}
          </div>
          <div className="flex-1" />
          <Button
            variant="outline" size="sm" className="h-9 text-xs gap-1.5 border-slate-200 hover:bg-slate-50"
            onClick={downloadCSV} data-testid="by-unit-download-csv" disabled={loading || monthCount === 0}
          >
            <Download className="h-3.5 w-3.5" /> Export CSV
          </Button>
        </div>
        <div className="px-4 py-2 bg-slate-50/70 text-[11px] text-slate-600 flex items-center gap-3 border-b border-slate-200">
          <TrendingUp className="h-3.5 w-3.5 text-slate-400" />
          <span>{monthCount} month{monthCount === 1 ? '' : 's'} selected</span>
          <span className="text-slate-300">•</span>
          <span>Grand total <strong className="text-slate-900 font-semibold tabular-nums">${fmt(grandTotal)}</strong></span>
          <span className="text-slate-300">•</span>
          <span>Monthly average <strong className="text-slate-900 font-semibold tabular-nums">${fmt(grandAvg)}</strong></span>
        </div>

        {/* Grid */}
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="bg-slate-50/70 border-b border-slate-200">
                <th className="sticky left-0 z-10 bg-slate-50/70 px-4 py-2.5 text-left text-[10px] uppercase tracking-wider text-slate-500 font-semibold">Property / Unit</th>
                {cols.map((c) => (
                  <th key={`${c.year}-${c.month}`} className="px-3 py-2.5 text-right text-[10px] uppercase tracking-wider text-slate-500 font-semibold whitespace-nowrap">
                    {monthLabel(c.year, c.month)}
                  </th>
                ))}
                <th className="px-3 py-2.5 text-right text-[10px] uppercase tracking-wider text-slate-700 font-semibold bg-slate-100 whitespace-nowrap">Total</th>
                <th className="px-3 py-2.5 text-right text-[10px] uppercase tracking-wider text-slate-700 font-semibold bg-slate-100 whitespace-nowrap">Avg / mo</th>
              </tr>
            </thead>
            <tbody>
              {propertyRows.length === 0 && !loading && (
                <tr><td colSpan={cols.length + 3} className="text-center py-8 text-slate-400 text-sm italic">No income in the selected range.</td></tr>
              )}
              {propertyRows.map((p, pi) => {
                // Property subtotal per column
                const propCols = cols.map((c) => p.units.reduce((s, u) => s + (u.cells[`${c.year}-${c.month}`] || 0), 0));
                const propTotal = propCols.reduce((a, b) => a + b, 0);
                const rows = [];
                rows.push(
                  <tr key={`p-${p.property_id}-${pi}`} className="bg-slate-50/40 border-t border-slate-200">
                    <td className="sticky left-0 bg-slate-50/40 px-4 py-2 text-sm font-semibold text-slate-800">{p.property_name}</td>
                    {propCols.map((v, i) => (
                      <td key={i} className="px-3 py-2 text-right text-sm font-semibold text-slate-700 tabular-nums">
                        {v > 0 ? `$${fmt(v)}` : <span className="text-slate-300">—</span>}
                      </td>
                    ))}
                    <td className="px-3 py-2 text-right text-sm font-semibold text-slate-900 tabular-nums bg-slate-100">${fmt(propTotal)}</td>
                    <td className="px-3 py-2 text-right text-sm font-semibold text-slate-900 tabular-nums bg-slate-100">${fmt(propTotal / monthCount)}</td>
                  </tr>
                );
                for (const u of p.units) {
                  const cells = cols.map((c) => u.cells[`${c.year}-${c.month}`] || 0);
                  const total = cells.reduce((a, b) => a + b, 0);
                  rows.push(
                    <tr key={`u-${u.unit_id}`} className="border-t border-slate-100 hover:bg-slate-50/60 transition-colors">
                      <td className="sticky left-0 bg-white hover:bg-slate-50/60 px-4 py-1.5 pl-8 text-sm text-slate-700">Unit {u.unit_number}</td>
                      {cells.map((v, i) => (
                        <td key={i} className="px-3 py-1.5 text-right text-sm text-slate-700 tabular-nums">
                          {v > 0 ? `$${fmt(v)}` : <span className="text-slate-300">—</span>}
                        </td>
                      ))}
                      <td className="px-3 py-1.5 text-right text-sm text-slate-900 font-medium tabular-nums bg-slate-50">${fmt(total)}</td>
                      <td className="px-3 py-1.5 text-right text-sm text-slate-900 font-medium tabular-nums bg-slate-50">${fmt(total / monthCount)}</td>
                    </tr>
                  );
                }
                return rows;
              })}
            </tbody>
            {propertyRows.length > 0 && (
              <tfoot>
                <tr className="border-t-2 border-slate-800 bg-slate-900 text-white">
                  <td className="sticky left-0 bg-slate-900 px-4 py-2.5 text-[11px] uppercase tracking-wider font-semibold">Monthly Totals</td>
                  {colTotals.map((v, i) => (
                    <td key={i} className="px-3 py-2.5 text-right text-sm font-semibold tabular-nums">${fmt(v)}</td>
                  ))}
                  <td className="px-3 py-2.5 text-right text-sm font-bold tabular-nums bg-slate-950">${fmt(grandTotal)}</td>
                  <td className="px-3 py-2.5 text-right text-sm font-bold tabular-nums bg-slate-950">${fmt(grandAvg)}</td>
                </tr>
              </tfoot>
            )}
          </table>
        </div>
        {loading && <div className="px-4 py-3 text-sm text-slate-500 italic">Loading…</div>}
      </div>
    </div>
  );
}
